import axios from 'axios';

// Multi-transport login helper.
//
// Some client-side security suites (antivirus with HTTPS scanning, corporate
// proxies) intercept and DROP POST requests whose URL/payload matches
// phishing heuristics. On those clients every attempt to `/api/*login*`,
// `/api/*session*` or `/api/*auth*` fails with ERR_NETWORK before it even
// leaves the machine — the server never sees it.
//
// This helper tries three transports in order and returns as soon as one
// succeeds. Every path validates the same bcrypt hash server-side; this is a
// TRANSPORT workaround, not a security bypass.
//
//   1. POST /api/session  or  /api/influs/session   (form-urlencoded)
//   2. PUT  /api/config/sync?s=a|i                  (text/plain body, base64)
//   3. GET  /api/config/sync?s=a|i&t=<base64>       (last-resort: GET)
//
// scope: 'a' → /vtas dashboard, 'i' → /influs CRM, 'p' → unified /panel hub.
// remember: opt in to a 7-day session instead of the default 30-minute sliding.
export const robustLogin = async (password, scope = 'a', remember = false) => {
  const cfg = { withCredentials: true, timeout: 10000 };
  const primary =
    scope === 'i' ? '/api/influs/session'
    : scope === 'p' ? '/api/panel/login'
    : '/api/session';
  const b64 = (typeof window !== 'undefined' && window.btoa)
    ? window.btoa(unescape(encodeURIComponent(password)))
    : Buffer.from(password, 'utf8').toString('base64');
  const r = remember ? '1' : '';

  // 1) Standard POST (works for 99% of users)
  try {
    // Panel login uses JSON body ({"password", "remember"}) instead of form-urlencoded.
    if (scope === 'p') {
      const res = await axios.post(primary, { password, remember }, cfg);
      return { data: res.data, method: 'post-json' };
    }
    const params = new URLSearchParams();
    params.append('password', password);
    if (remember) params.append('remember', '1');
    const res = await axios.post(primary, params, cfg);
    return { data: res.data, method: 'post-form' };
  } catch (err) {
    if (err?.response) throw err;
  }

  // 2) PUT stealth — text/plain body, base64 payload, neutral URL
  try {
    const url = `/api/config/sync?s=${scope}${r ? `&r=${r}` : ''}`;
    const res = await axios.put(url, b64, {
      ...cfg, headers: { 'Content-Type': 'text/plain' },
    });
    return { data: res.data, method: 'put-stealth' };
  } catch (err) {
    if (err?.response) throw err;
  }

  // 3) GET stealth — no body at all, base64 in querystring
  try {
    const url = `/api/config/sync?s=${scope}&t=${encodeURIComponent(b64)}${r ? `&r=${r}` : ''}`;
    const res = await axios.get(url, cfg);
    return { data: res.data, method: 'get-stealth' };
  } catch (err) {
    if (err?.response) throw err;
  }

  // 4) Fresh-URL fallbacks (2026-07-20). Some AVs blocklist "sync"/"config"
  // too. These use neutral URLs and try both querystring AND a temp cookie
  // (Cookie header is very rarely inspected). Also cycle through GET/PUT/POST
  // and shuffle URLs so at least one combination slips through.
  const freshUrls = ['/api/i/echo', '/api/tick'];
  for (const base of freshUrls) {
    const qs = `?s=${scope}${r ? `&r=${r}` : ''}`;
    // (a) GET with token in querystring
    try {
      const res = await axios.get(`${base}${qs}&t=${encodeURIComponent(b64)}`, cfg);
      return { data: res.data, method: `get-x@${base}` };
    } catch (err) {
      if (err?.response) throw err;
    }
    // (b) GET with token in a temp cookie (Cookie header, no querystring/body).
    // Set-then-clear via document.cookie — path scoped so it doesn't leak elsewhere.
    if (typeof document !== 'undefined') {
      try {
        document.cookie = `_tk=${b64}; path=/api; SameSite=Lax; Secure`;
        const res = await axios.get(`${base}${qs}`, cfg);
        // Server-side deletes _tk on any handler that reads it.
        return { data: res.data, method: `get-tk@${base}` };
      } catch (err) {
        if (err?.response) throw err;
      } finally {
        document.cookie = '_tk=; path=/api; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      }
    }
    // (c) PUT with body — different verb often bypasses POST-focused rules.
    try {
      const res = await axios.put(`${base}${qs}`, b64, {
        ...cfg, headers: { 'Content-Type': 'text/plain' },
      });
      return { data: res.data, method: `put-x@${base}` };
    } catch (err) {
      if (err?.response) throw err;
    }
  }

  // If we get here everything failed at network level.
  throw new Error('Todos los transportes fallaron — posible antivirus bloqueando la conexión');
};
